"use client";

import React, { useState, useEffect, useRef } from "react";
import { X, FileText, Edit, LibraryBig, Settings, ChevronLeft } from "lucide-react";
import { StructureDefinition } from "./types";
import { PromptResponseItem } from "@/types";
import { getCurrentUser } from "aws-amplify/auth";
import { getFormats, type Format } from "@/lib/clients/formats";

export type OptionType = "custom-prompt" | "prompt-library" | "structure-definition";

interface OptionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOption: OptionType | null;
  onOptionSelect: (option: OptionType) => void;
  // カスタムプロンプト関連
  promptValue: string;
  onPromptChange: (value: string) => void;
  // 構造定義関連
  structureDefinitions?: StructureDefinition[];
  selectedStructureDefinitionId?: string;
  onStructureDefinitionChange: (id: string) => void;
}

export const OptionsPanel: React.FC<OptionsPanelProps> = ({
  isOpen,
  onClose,
  selectedOption,
  onOptionSelect,
  promptValue,
  onPromptChange,
  structureDefinitions = [],
  selectedStructureDefinitionId,
  onStructureDefinitionChange,
}) => {
  const [promptLibrary, setPromptLibrary] = useState<PromptResponseItem[]>([]);
  const [formats, setFormats] = useState<Format[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormatsLoading, setIsFormatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formatsError, setFormatsError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // 認証ヘッダーを取得
  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    try {
      await getCurrentUser(); // ユーザーが存在することを確認
      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (idToken) {
        console.log("🔐 OptionsPanel - idToken取得成功");
        return {
          "Content-Type": "application/json",
          "Authorization": idToken,
        };
      } else {
        console.warn("🔐 OptionsPanel - idToken取得失敗");
        return {
          "Content-Type": "application/json",
        };
      }
    } catch (error) {
      console.error("🔐 OptionsPanel - 認証ヘッダー取得エラー:", error);
      return {
        "Content-Type": "application/json",
      };
    }
  };

  // プロンプトライブラリの取得
  const fetchPromptLibrary = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const headers = await getAuthHeaders();
      console.log("🔐 OptionsPanel - リクエストヘッダー:", headers);
      console.log("🔐 OptionsPanel - Authorizationヘッダー存在:", !!headers.Authorization);
      console.log("🔐 OptionsPanel - Authorizationヘッダー長さ:", headers.Authorization?.length || 0);

      const response = await fetch('/api/custom-prompts-handler', {
        method: 'POST',
        headers,
        body: JSON.stringify({ operation: 'GETALL' })
      });
      
      console.log("🔐 OptionsPanel - レスポンスステータス:", response.status);
      console.log("🔐 OptionsPanel - レスポンスヘッダー:", Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'プロンプトの取得に失敗しました');
      }
      
      if (!data.data || !Array.isArray(data.data)) {
        throw new Error('レスポンスデータの形式が正しくありません');
      }
      
      setPromptLibrary(data.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // フォーマットの取得
  const fetchFormats = async () => {
    setIsFormatsLoading(true);
    setFormatsError(null);
    
    try {
      console.log("🔍 OptionsPanel - フォーマット取得開始");
      const formatList = await getFormats();
      console.log("🔍 OptionsPanel - 取得されたフォーマット:", formatList);
      setFormats(formatList);
    } catch (err) {
      console.error("🔍 OptionsPanel - フォーマット取得エラー:", err);
      setFormatsError((err as Error).message);
    } finally {
      setIsFormatsLoading(false);
    }
  };

  // プロンプトライブラリの検索フィルタリング
  const filteredPrompts = promptLibrary.filter(prompt => 
    (prompt.prompt_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (prompt.prompt || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // プロンプトの選択
  const selectPrompt = (prompt: PromptResponseItem) => {
    onPromptChange(prompt.prompt);
    onClose();
  };

  // オプション選択時の処理
  const handleOptionSelect = (option: OptionType) => {
    onOptionSelect(option);
    
    // プロンプトライブラリ選択時にデータをロード
    if (option === "prompt-library" && promptLibrary.length === 0) {
      fetchPromptLibrary();
    }

    // フォーマット選択時にデータをロード
    if (option === "structure-definition" && formats.length === 0) {
      fetchFormats();
    }
  };

  // キーボードショートカット（Escape、Ctrl+Enter）
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.ctrlKey && e.key === 'Enter' && selectedOption === 'custom-prompt' && promptValue.trim()) {
      onClose();
    }
  };

  // パネル外クリックで閉じる（オプションボタンは除外）
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // オプションボタンかどうかをチェック
      const isOptionsButton = target.closest('[aria-label="オプション設定"]');
      
      if (panelRef.current && 
          !panelRef.current.contains(target as Node) && 
          !isOptionsButton) {
        onClose();
      }
    };

    if (isOpen) {
      // 少し遅延させてイベントリスナーを追加
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // フォーカス管理
  useEffect(() => {
    if (isOpen && selectedOption === 'custom-prompt' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, selectedOption]);

  // 戻るボタンハンドラー
  const handleBack = () => {
    onOptionSelect(null as unknown as OptionType);
  };

  return (
    <div 
      ref={panelRef}
      className={`absolute bottom-full left-0 mb-4 bg-white dark:bg-gray-900 rounded-xl max-w-xs shadow-2xl z-[100] overflow-hidden transition-all duration-500 ease-out transform origin-bottom ${
        isOpen 
          ? 'opacity-100 translate-y-0 scale-100 visible' 
          : 'opacity-0 translate-y-4 scale-95 invisible'
      }`}
      style={{
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transitionProperty: 'opacity, transform, visibility',
        transitionDuration: '400ms',
        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        transformOrigin: 'bottom center',
      }}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ヘッダー */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          {selectedOption ? (
            /* 戻るボタン */
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBack();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-300 hover:text-primary transition-colors duration-200 group mr-2 pointer-events-auto"
            >
              <ChevronLeft className="h-3.5 w-3.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
              <span>戻る</span>
            </button>
          ) : (
            <Settings className="h-4 w-4 text-primary" />
          )}
          <h3 className="text-sm font-semibold text-foreground">
            {selectedOption === "custom-prompt" && "カスタムプロンプト"}
            {selectedOption === "prompt-library" && "プロンプトライブラリ"}
            {selectedOption === "structure-definition" && "フォーマット"}
            {!selectedOption && "オプション設定"}
          </h3>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 pointer-events-auto"
          aria-label="設定を閉じる"
        >
          <X className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {/* コンテンツ */}
      <div className="p-4 relative overflow-hidden">
        {!selectedOption ? (
          /* オプション選択画面 */
          <div 
            className="transition-all duration-300 ease-out transform"
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? 'translateY(0)' : 'translateY(8px)',
              transitionDelay: isOpen ? '100ms' : '0ms'
            }}
            key="option-list"
          >
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
              設定したい項目を選択してください
            </p>
            
            <section className="overflow-y-auto">
            {/* カスタムプロンプト（テキスト入力） */}
            <button
              onClick={() => handleOptionSelect("custom-prompt")}
              className="w-full p-3 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="カスタムプロンプトを選択"
            >
              <div className="flex items-center space-x-3">
                <Edit className="h-4 w-4 text-primary" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">カスタムプロンプト</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    独自の指示をテキストで入力
                  </p>
                </div>
              </div>
            </button>

            {/* プロンプトライブラリから選択 */}
            <button
              onClick={() => handleOptionSelect("prompt-library")}
              className="w-full p-3 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="プロンプトライブラリを選択"
            >
              <div className="flex items-center space-x-3">
                <LibraryBig className="h-4 w-4 text-primary" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">プロンプトライブラリ</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    保存済みのプロンプトから選択
                  </p>
                </div>
              </div>
            </button>

            {/* フォーマット */}
            <button
              onClick={() => handleOptionSelect("structure-definition")}
              className="w-full p-3 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="フォーマットを選択"
            >
              <div className="flex items-center space-x-3">
                <FileText className="h-4 w-4 text-primary" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">フォーマット</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    レポートの出力形式を指定
                  </p>
                </div>
              </div>
            </button>
            </section>
          </div>
        ) : (
          /* 選択されたオプションの設定画面 */
          <div 
            className="transition-all duration-300 ease-out transform"
            style={{
              opacity: selectedOption ? 1 : 0,
              transform: selectedOption ? 'translateY(0)' : 'translateY(8px)',
              transitionDelay: selectedOption ? '100ms' : '0ms'
            }}
            key={`option-${selectedOption}`}
          >
            {selectedOption === "custom-prompt" && (
              <div 
                className="space-y-3 transition-all duration-200 ease-out"
                style={{
                  opacity: selectedOption === "custom-prompt" ? 1 : 0,
                  transform: selectedOption === "custom-prompt" ? 'translateY(0)' : 'translateY(4px)',
                  transitionDelay: selectedOption === "custom-prompt" ? '150ms' : '0ms'
                }}
              >
                <textarea
                  ref={textareaRef}
                  value={promptValue}
                  onChange={(e) => onPromptChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary min-h-24 resize-none transition-all duration-200"
                  placeholder="例：「ミーティングの主な決定事項とアクションアイテムをまとめてください」"
                />
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    音声処理時に使用する特別な指示
                    <br /><span className="text-gray-400 dark:text-gray-500">(Ctrl + Enter で設定完了)</span>
                  </p>
                  <button
                    onClick={onClose}
                    disabled={!promptValue.trim()}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                      promptValue.trim()
                        ? 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    設定完了
                  </button>
                </div>
              </div>
            )}

            {selectedOption === "prompt-library" && (
              <div 
                className="space-y-3 transition-all duration-200 ease-out"
                style={{
                  opacity: selectedOption === "prompt-library" ? 1 : 0,
                  transform: selectedOption === "prompt-library" ? 'translateY(0)' : 'translateY(4px)',
                  transitionDelay: selectedOption === "prompt-library" ? '150ms' : '0ms'
                }}
              >
                {/* 検索バー */}
                <input
                  type="text"
                  placeholder="プロンプトを検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isLoading}
                  className={`w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 ${
                    isLoading ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''
                  }`}
                />
                
                {/* プロンプトリスト */}
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
                  {isLoading ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {/* スケルトンプロンプトアイテム */}
                      {[...Array(3)].map((_, index) => (
                        <div key={index} className="p-2.5 animate-pulse">
                          <div className="space-y-1">
                            {/* プロンプト名のスケルトン（異なる幅で自然な見た目に） */}
                            <div 
                              className="h-3 bg-gray-200 dark:bg-gray-700 rounded"
                              style={{ width: index === 0 ? '65%' : index === 1 ? '80%' : '70%' }}
                            ></div>
                            {/* プロンプト内容のスケルトン（2行表示で実際のプロンプトに近づける） */}
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                            <div 
                              className="h-3 bg-gray-200 dark:bg-gray-700 rounded"
                              style={{ width: index === 0 ? '85%' : index === 1 ? '60%' : '75%' }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : error ? (
                    <div className="p-3 text-center text-xs text-red-500">
                      {error}
                      <button
                        onClick={fetchPromptLibrary}
                        className="ml-2 text-primary hover:underline transition-colors duration-200"
                        aria-label="再試行"
                      >
                        再試行
                      </button>
                    </div>
                  ) : filteredPrompts.length === 0 ? (
                    <div className="p-3 text-center text-xs text-gray-500">
                      {promptLibrary.length === 0 ? 'プロンプトが登録されていません' : '一致するプロンプトがありません'}
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredPrompts.map((prompt) => (
                        <li key={prompt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200">
                          <button
                            onClick={() => selectPrompt(prompt)}
                            className="w-full p-2.5 text-left transition-colors duration-200"
                            aria-label={`プロンプトを選択: ${prompt.prompt_name}`}
                          >
                            <p className="text-xs font-medium truncate">{prompt.prompt_name}</p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">{prompt.prompt}</p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {selectedOption === "structure-definition" && (
              <div 
                className="space-y-3 transition-all duration-200 ease-out"
                style={{
                  opacity: selectedOption === "structure-definition" ? 1 : 0,
                  transform: selectedOption === "structure-definition" ? 'translateY(0)' : 'translateY(4px)',
                  transitionDelay: selectedOption === "structure-definition" ? '150ms' : '0ms'
                }}
              >
                {isFormatsLoading ? (
                  <div className="space-y-3 animate-pulse">
                    {/* セレクトボックスのスケルトン */}
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-md w-full"></div>
                    {/* 説明テキストのスケルトン */}
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  </div>
                ) : formatsError ? (
                  <div className="p-3 text-center text-xs text-red-500">
                    {formatsError}
                    <button
                      onClick={fetchFormats}
                      className="ml-2 text-primary hover:underline transition-colors duration-200"
                      aria-label="再試行"
                    >
                      再試行
                    </button>
                  </div>
                ) : formats.length === 0 && structureDefinitions.length === 0 ? (
                  <div className="p-3 text-center text-xs text-gray-500">
                    フォーマットが登録されていません
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedStructureDefinitionId || ""}
                      onChange={(e) => {
                        onStructureDefinitionChange(e.target.value);
                        onClose();
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                    >
                      <option value="">フォーマットを選択してください</option>
                      
                      {/* API から取得したformats */}
                      {formats.length > 0 && (
                        <optgroup label="利用可能なフォーマット">
                          {formats.map((format) => (
                            <option key={format.id} value={format.id}>
                              {selectedStructureDefinitionId === format.id ? `✓ ${format.name} (選択中)` : format.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      
                      {/* 既存のstructureDefinitions（後方互換性のため） */}
                      {structureDefinitions.length > 0 && (
                        <optgroup label="構造定義">
                          {(() => {
                            // 作成日時で降順ソート（最新が最初）
                            const sortedDefinitions = [...structureDefinitions].sort((a, b) => {
                              if (!a.created_at && !b.created_at) return 0;
                              if (!a.created_at) return 1;
                              if (!b.created_at) return -1;
                              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                            });
                            
                            return sortedDefinitions.map((def) => (
                              <option key={def.id} value={def.id}>
                                {selectedStructureDefinitionId === def.id ? `✓ ${def.name} (選択中)` : def.name}
                              </option>
                            ));
                          })()}
                        </optgroup>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      レポートの出力形式を選択できます
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 